import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, PlayCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Empty } from "@/components/ui-bits";

const DIFF = { beginner: "ok", intermediate: "warn", advanced: "bad" };

export const ExerciseCard = ({ ex, onWatched, highlight, compact }) => {
  const [open, setOpen] = useState(!compact);
  return (
    <div className={`excard ${highlight ? "hi" : ""}`} data-testid={`exercise-${ex.id}`}>
      <div className="ph" style={{ marginBottom: 6, cursor: compact ? "pointer" : "default" }} onClick={() => compact && setOpen(!open)}>
        <div>
          <div className="st">{ex.name}{highlight && <span className="chip warn" style={{ marginLeft: 8 }}><span className="dot" />Pre-session viewing</span>}</div>
          <div className="ss" style={{ textTransform: "capitalize" }}>{ex.muscleGroup} · {ex.category} · <span className={`chip ${DIFF[ex.difficulty]}`}><span className="dot" />{ex.difficulty}</span></div>
        </div>
        {ex.watchedAt ? <span className="chip ok" data-testid={`exercise-watched-${ex.id}`}><CheckCircle2 size={12} />Watched</span> : onWatched && <span className="chip gray"><PlayCircle size={12} />Not yet</span>}
      </div>
      {open && (
        <>
          <div className="video"><iframe src={ex.embedUrl} title={ex.name} loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" /></div>
          {ex.description && <div className="msg" style={{ marginTop: 10 }}><div className="who"><span>Form &amp; method</span></div><div className="txt">{ex.description}</div></div>}
          {ex.tags?.length > 0 && <div className="hl">{ex.tags.map((t) => <span key={t} className="chip gray">#{t}</span>)}</div>}
          {onWatched && !ex.watchedAt && <button className="btn sm" style={{ marginTop: 10 }} onClick={() => onWatched(ex)} data-testid={`exercise-mark-watched-${ex.id}`}>Mark as watched</button>}
        </>
      )}
    </div>
  );
};

export const ClientLibrary = ({ exercises, onChanged, prepIds = [] }) => {
  const [group, setGroup] = useState("muscle");
  const [filter, setFilter] = useState("all");
  const markWatched = async (ex) => {
    try { await api.post(`/client/me/exercises/${ex.id}/watched`); toast.success("Nice — marked as watched"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!exercises.length) return <Empty>Your coach hasn't assigned exercises yet.</Empty>;
  const key = group === "muscle" ? "muscleGroup" : "category";
  const keys = [...new Set(exercises.map((e) => e[key]))];
  const shown = filter === "all" ? exercises : exercises.filter((e) => e[key] === filter);
  const prep = exercises.filter((e) => prepIds.includes(e.id));
  return (
    <div data-testid="client-library">
      {prep.length > 0 && <div style={{ marginBottom: 14 }}>{prep.map((e) => <ExerciseCard key={e.id} ex={e} highlight compact onWatched={markWatched} />)}</div>}
      <div className="tabs">
        <button className={group === "muscle" ? "on" : ""} onClick={() => { setGroup("muscle"); setFilter("all"); }} data-testid="library-group-muscle">By muscle group</button>
        <button className={group === "category" ? "on" : ""} onClick={() => { setGroup("category"); setFilter("all"); }} data-testid="library-group-category">By category</button>
      </div>
      <div className="tabs">
        <button className={filter === "all" ? "on" : ""} onClick={() => setFilter("all")} data-testid="library-filter-all">All ({exercises.length})</button>
        {keys.map((k) => <button key={k} className={filter === k ? "on" : ""} style={{ textTransform: "capitalize" }} onClick={() => setFilter(k)} data-testid={`library-filter-${k.replace(/ /g, "-")}`}>{k} ({exercises.filter((e) => e[key] === k).length})</button>)}
      </div>
      {shown.filter((e) => !prepIds.includes(e.id)).map((e) => <ExerciseCard key={e.id} ex={e} compact onWatched={markWatched} />)}
    </div>
  );
};
